import { create } from "zustand";
import type { StateCreator } from "zustand";
import type {
  SubscriptionLifecycleStatus,
  SubscriptionSnapshot,
} from "@um/ui/services/revenueCat";

export type { SubscriptionLifecycleStatus, SubscriptionSnapshot };

/** Drives `PaywallHost` so web `presentPaywall` receives a mounted panel element. */
export type WebPaywallHostSession = {
  sessionId: number;
  resolvePanel: (panel: HTMLElement) => void;
};

export type WebPurchaseRedemptionOutcome =
  | { kind: "idle" }
  | { kind: "success" }
  | { kind: "expired"; obfuscatedEmail: string }
  | { kind: "wrong_user" }
  | { kind: "invalid" }
  | { kind: "error"; message: string };

/** Drives the native web-purchase redemption dialog in `PaywallHost`. */
export type WebPurchaseRedemptionSession = {
  sessionId: number;
  url: string;
  outcome: WebPurchaseRedemptionOutcome;
  isRedeeming: boolean;
};

export interface PaymentsState {
  /**
   * One snapshot per subscription entitlement tier the user has held
   * (Character Support / Clinical Support), ordered base tier first. Empty
   * when there are no subscription entitlements on record.
   */
  subscriptions: SubscriptionSnapshot[];
  subscriptionsLoading: boolean;
  /** Epoch ms of last successful `refreshActiveSubscriptions` completion. */
  subscriptionsLastFetchedAt: number | null;

  /**
   * From RevenueCat `paywallDisplayed` / `paywallDismissed` (paywall only).
   */
  isPaywallVisible: boolean;
  /**
   * True while any RevenueCat paywall or customer center flow we started is in flight
   * (until the native sheet is dismissed and the promise settles).
   */
  isRevenueCatModalPresenting: boolean;

  /**
   * When non-null, the web paywall host renders a phone-width shell and calls
   * `resolvePanel` with the panel element for RevenueCat `htmlTarget`.
   */
  webPaywallHostSession: WebPaywallHostSession | null;

  /** Non-null while the RevenueCat web-purchase redemption dialog is open. */
  webPurchaseRedemptionSession: WebPurchaseRedemptionSession | null;

  /**
   * True after `usePayments` has finished the RevenueCat session sync for the
   * signed-in user (configure / logIn / changeUser and related setup).
   */
  isReady: boolean;

  setSubscriptions: (subs: SubscriptionSnapshot[]) => void;
  setSubscriptionsLoading: (loading: boolean) => void;
  setSubscriptionsLastFetchedAt: (t: number | null) => void;
  setIsPaywallVisible: (visible: boolean) => void;
  setRevenueCatModalPresenting: (presenting: boolean) => void;
  setWebPaywallHostSession: (session: WebPaywallHostSession | null) => void;
  setWebPurchaseRedemptionSession: (
    session: WebPurchaseRedemptionSession | null
  ) => void;
  patchWebPurchaseRedemptionSession: (
    patch: Partial<
      Pick<WebPurchaseRedemptionSession, "outcome" | "isRedeeming">
    >
  ) => void;
  setIsReady: (ready: boolean) => void;
}

const storeCreator: StateCreator<PaymentsState> = (set) => ({
  subscriptions: [],
  subscriptionsLoading: false,
  subscriptionsLastFetchedAt: null,
  isPaywallVisible: false,
  isRevenueCatModalPresenting: false,
  webPaywallHostSession: null,
  webPurchaseRedemptionSession: null,
  isReady: false,

  setSubscriptions: (subscriptions) => set({ subscriptions }),
  setSubscriptionsLoading: (subscriptionsLoading) => set({ subscriptionsLoading }),
  setSubscriptionsLastFetchedAt: (subscriptionsLastFetchedAt) =>
    set({ subscriptionsLastFetchedAt }),
  setIsPaywallVisible: (isPaywallVisible) => set({ isPaywallVisible }),
  setRevenueCatModalPresenting: (isRevenueCatModalPresenting) =>
    set({ isRevenueCatModalPresenting }),
  setWebPaywallHostSession: (webPaywallHostSession) =>
    set({ webPaywallHostSession }),
  setWebPurchaseRedemptionSession: (webPurchaseRedemptionSession) =>
    set({ webPurchaseRedemptionSession }),
  patchWebPurchaseRedemptionSession: (patch) =>
    set((state) => {
      if (!state.webPurchaseRedemptionSession) return state;
      return {
        webPurchaseRedemptionSession: {
          ...state.webPurchaseRedemptionSession,
          ...patch,
        },
      };
    }),
  setIsReady: (isReady) => set({ isReady }),
});

export const usePaymentsStore = create<PaymentsState>(storeCreator);

/** True if a paywall is on screen (SDK) or we are presenting any payment modal. */
export function selectIsPaymentUiOpen(state: PaymentsState): boolean {
  return state.isPaywallVisible || state.isRevenueCatModalPresenting;
}

/** True while the web-purchase redemption dialog is open. */
export function selectIsWebPurchaseRedemptionOpen(
  state: PaymentsState
): boolean {
  return state.webPurchaseRedemptionSession != null;
}
